import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Chip,
  Button,
  Pagination,
  Stack,
  InputAdornment,
  CircularProgress,
  Paper,
  Divider,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Search,
  LocationOn,
  Clear,
  FilterList,
  ExpandMore,
  ExpandLess,
  Home,
  Add,
} from '@mui/icons-material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useApiGet } from '../../utils/httpClientTs';
import { getBedAndBreakfasts } from '../../endpoints/bedandbreakfast';
import {
  BedAndBreakfastMinimal,
  BnBSearchParams,
} from '../../types/bedandbreakfast';
import SPDSelector from '../../Components/SPDSelector';
import ChapterSelector from '../../Components/ChapterSelector';
import FeatureDescription from '../../Components/FeatureDescription';
import { roles } from '../../utils/variables';
import AlertItem from '../../Components/AlertItem';
import { BNB_DESCRIPTION } from '../../constants/featureDescriptions';

/** Stable query string for cache keys and effects (order-insensitive). */
function canonicalBnBListSearchString(sp: URLSearchParams): string {
  const keys = [...new Set([...sp.keys()])].sort();
  const pairs: string[] = [];
  for (const k of keys) {
    for (const v of [...sp.getAll(k)].sort()) {
      pairs.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
    }
  }
  return pairs.join('&');
}

/** Build API params from the current URL (same keys as GET /bed-and-breakfast/list). */
function bnbListParamsFromSearchParams(sp: URLSearchParams): BnBSearchParams {
  const page = Math.max(1, parseInt(sp.get('page') || '1', 10) || 1);
  const per_page = Math.max(1, parseInt(sp.get('per_page') || '12', 10) || 12);
  const search = sp.get('search')?.trim() || undefined;
  const spd_id = sp.get('spd_id')?.trim() || undefined;
  const local_chapter_id = sp.get('local_chapter_id')?.trim() || undefined;
  const fee = sp.get('fee_filter')?.trim();
  const fee_filter = fee && fee !== 'all' ? fee : undefined;
  const ia = sp.get('is_active');
  let is_active: boolean | undefined;
  if (ia === 'true') is_active = true;
  else if (ia === 'false') is_active = false;
  return {
    page,
    per_page,
    search,
    spd_id,
    local_chapter_id,
    is_active,
    fee_filter,
  };
}

const BnBList: React.FC = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [spd, setSpd] = useState('');
  const [selectedChapter, setSelectedChapter] = useState<any>(null);
  const [isActiveFilter, setIsActiveFilter] = useState<'all' | boolean>('all');
  const [feeFilter, setFeeFilter] = useState<string>('all');

  const urlKey = useMemo(
    () => canonicalBnBListSearchString(new URLSearchParams(search)),
    [search]
  );
  const requestParams = useMemo(
    () => bnbListParamsFromSearchParams(new URLSearchParams(search)),
    [search]
  );

  const applyUrl = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      const next = new URLSearchParams(urlSearchParams);
      mutate(next);
      setUrlSearchParams(next);
    },
    [urlSearchParams, setUrlSearchParams]
  );

  // Hydrate local UI when the URL changes (deep link, browser back from detail, etc.)
  useEffect(() => {
    const sp = new URLSearchParams(urlKey);
    setSearchTerm(sp.get('search') || '');
    setSpd(sp.get('spd_id') || '');
    const lc = sp.get('local_chapter_id');
    setSelectedChapter(lc ? { id: lc } : null);
    const ia = sp.get('is_active');
    if (ia === 'true') setIsActiveFilter(true);
    else if (ia === 'false') setIsActiveFilter(false);
    else setIsActiveFilter('all');
    setFeeFilter(sp.get('fee_filter') || 'all');
  }, [urlKey]);

  const {
    data: bnbData,
    isLoading,
    error,
    refetch,
  } = useApiGet(
    ['bed-and-breakfasts', urlKey],
    () => getBedAndBreakfasts(requestParams),
    {
      enabled: true,
      retry: 1,
      refetchOnMount: 'always',
    }
  );

  const handleSearch = () => {
    applyUrl((p) => {
      const t = searchTerm.trim();
      if (t) p.set('search', t);
      else p.delete('search');
      p.delete('page');
    });
  };

  const handleSpdChange = (value: string) => {
    setSpd(value);
    setSelectedChapter(null);
    applyUrl((p) => {
      const v = value.trim();
      if (v) p.set('spd_id', v);
      else p.delete('spd_id');
      p.delete('local_chapter_id');
      p.delete('page');
    });
  };

  const handleChapterChange = (chapter: any) => {
    setSelectedChapter(chapter);
    applyUrl((p) => {
      const raw = chapter?.id;
      const id =
        raw != null && String(raw).trim() !== '' ? String(raw).trim() : '';
      if (id) p.set('local_chapter_id', id);
      else p.delete('local_chapter_id');
      p.delete('page');
    });
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    applyUrl((p) => {
      if (page <= 1) p.delete('page');
      else p.set('page', String(page));
    });
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSpd('');
    setSelectedChapter(null);
    setIsActiveFilter('all');
    setFeeFilter('all');
    setUrlSearchParams(new URLSearchParams());
  };

  const handleIsActiveChange = (value: 'all' | boolean) => {
    setIsActiveFilter(value);
    applyUrl((p) => {
      if (value === 'all') p.delete('is_active');
      else p.set('is_active', value ? 'true' : 'false');
      p.delete('page');
    });
  };

  const handleFeeFilterChange = (value: string) => {
    setFeeFilter(value);
    applyUrl((p) => {
      if (!value || value === 'all') p.delete('fee_filter');
      else p.set('fee_filter', value);
      p.delete('page');
    });
  };

  const handleBnBClick = (bnb: BedAndBreakfastMinimal) => {
    navigate(`/bnb/${bnb.id}`, { state: { fromBnBList: true } });
  };

  const canCreateBnB = (): boolean => {
    const userRole = localStorage.getItem('role');
    const allowedRoles = [roles.Admin, roles.Support, roles.Communications];
    return allowedRoles.includes(userRole || '');
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <AlertItem
            severity="error"
            description={`Error loading bed and breakfasts: ${error.message}`}
            onClose={() => {}}
            keepOpen={true}
          />
        </Box>
        <Button variant="contained" onClick={() => refetch()}>
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header: title and button on one row */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h4" component="h1">
          Bed & Breakfast Directory
        </Typography>
        {canCreateBnB() && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/bnb/create')}
            sx={{ height: 'fit-content' }}
          >
            Create New B&B
          </Button>
        )}
      </Box>

      <FeatureDescription
        shortText={BNB_DESCRIPTION.short}
        longText={BNB_DESCRIPTION.long}
      />

      <Box sx={{ mb: 3 }} />

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search by name, city, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={() => {
                        setSearchTerm('');
                        applyUrl((p) => {
                          p.delete('search');
                          p.set('page', '1');
                        });
                      }}
                    >
                      <Clear />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              variant="contained"
              onClick={handleSearch}
              startIcon={<Search />}
              sx={{ minWidth: { xs: '100%', md: 'auto', marginRight: 3 } }}
            >
              Search
            </Button>
            <Button
              variant="outlined"
              onClick={() => setShowFilters(!showFilters)}
              startIcon={<FilterList />}
              endIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
              sx={{ minWidth: { xs: '100%', md: 'auto', marginRight: 3 } }}
            >
              Filters
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              startIcon={<Clear />}
              sx={{ minWidth: { xs: '100%', md: 'auto' } }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>

        {/* Collapsible Filters */}
        <Collapse in={showFilters}>
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <SPDSelector
                  spd={spd}
                  handleSpdSelect={handleSpdChange}
                  keyId="bnb-list"
                  all={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <ChapterSelector
                  chapter={selectedChapter?.id}
                  handleChapterSelect={handleChapterChange}
                  spd={spd}
                  keyId="bnb-list"
                  label="Local Chapter"
                  objectReturned={true}
                  all={true}
                />
              </Grid>
              {bnbData?.has_fees_permission && (
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="bnb-is-active-label">Status</InputLabel>
                    <Select
                      labelId="bnb-is-active-label"
                      label="Status"
                      value={
                        isActiveFilter === 'all'
                          ? 'all'
                          : String(isActiveFilter)
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        handleIsActiveChange(
                          v === 'all' ? 'all' : v === 'true'
                        );
                      }}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="true">Active</MenuItem>
                      <MenuItem value="false">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {bnbData?.has_fees_permission && (
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="bnb-fee-filter-label">
                      Fees (membership year)
                    </InputLabel>
                    <Select
                      labelId="bnb-fee-filter-label"
                      label="Fees (membership year)"
                      value={feeFilter}
                      onChange={(e) => handleFeeFilterChange(e.target.value)}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="paid_this_year">Paid This Year</MenuItem>
                      <MenuItem value="unpaid_this_year">
                        Unpaid This Year
                      </MenuItem>
                      <MenuItem value="paid_last_year">Paid Last Year</MenuItem>
                      <MenuItem value="unpaid_last_year">
                        Unpaid Last Year
                      </MenuItem>
                      <MenuItem value="paid_next_year">Paid Next Year</MenuItem>
                      <MenuItem value="unpaid_next_year">
                        Unpaid Next Year
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Results */}
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">
              {bnbData?.pagination.total_count || 0} bed and breakfast
              {bnbData?.pagination.total_count !== 1 ? 's' : ''} found
            </Typography>
          </Box>

          {bnbData?.data.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No bed and breakfasts found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search criteria or filters
              </Typography>
            </Paper>
          ) : (
            <>
              <Grid container spacing={3}>
                {bnbData?.data.map((bnb) => (
                  <Grid item xs={12} md={6} lg={4} key={bnb.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                        },
                      }}
                      onClick={() => handleBnBClick(bnb)}
                    >
                      {/* Header with home icon */}
                      <Box
                        sx={{
                          backgroundColor: 'primary.veryLight',
                          p: 3,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                        }}
                      >
                        <Home
                          sx={{
                            fontSize: 60,
                            color: 'primary.main',
                          }}
                        />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="h5"
                            component="h2"
                            fontWeight="600"
                            gutterBottom
                            color="text.primary"
                          >
                            {bnb.name}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <LocationOn
                              sx={{ fontSize: 18, color: 'text.secondary' }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {bnb.local_chapter_name ||
                                'Location details available'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <CardContent
                        sx={{
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          p: 2.5,
                        }}
                      >
                        {bnb.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: 2,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: 1.6,
                            }}
                          >
                            {bnb.description}
                          </Typography>
                        )}

                        {!bnb.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2, fontStyle: 'italic' }}
                          >
                            View details to learn more about this property
                          </Typography>
                        )}

                        {bnb.amenities && bnb.amenities.length > 0 && (
                          <Box mb={2}>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              {bnb.amenities
                                .slice(0, 3)
                                .map((amenity, index) => (
                                  <Chip
                                    key={index}
                                    label={amenity}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                ))}
                              {bnb.amenities.length > 3 && (
                                <Chip
                                  label={`+${bnb.amenities.length - 3} more`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.75rem' }}
                                />
                              )}
                            </Stack>
                          </Box>
                        )}

                        {bnbData?.has_fees_permission &&
                          (bnb.is_active_tag || bnb.last_year_paid_fees) && (
                            <Box mb={2}>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                flexWrap="wrap"
                                useFlexGap
                              >
                                {bnb.is_active_tag && (
                                  <Chip
                                    label={bnb.is_active_tag}
                                    size="small"
                                    color={
                                      bnb.is_active_tag === 'Active'
                                        ? 'success'
                                        : 'default'
                                    }
                                    variant="outlined"
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                )}
                                {bnb.last_year_paid_fees && (
                                  <Chip
                                    label={`Fees: ${bnb.last_year_paid_fees}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                )}
                              </Stack>
                            </Box>
                          )}

                        <Box mt="auto">
                          <Divider sx={{ mb: 1 }} />
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography variant="body2" color="text.secondary">
                              {bnb.local_chapter_name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="primary"
                              fontWeight="medium"
                            >
                              View Details →
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              {bnbData &&
                bnbData.pagination.total_count >
                  bnbData.pagination.per_page && (
                  <Box display="flex" justifyContent="center" mt={4}>
                    <Pagination
                      count={bnbData.pagination.total_pages}
                      page={bnbData.pagination.page}
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                    />
                  </Box>
                )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default BnBList;
